import { RefObject, useLayoutEffect } from 'react';

import { ValueLabelBox, layoutValueLabels } from './valueLabelLayout';

/** Recharts can render after its parent through ResizeObserver. Resolve text geometry before the browser paints. */
export default function useValueLabelLayout(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const place = () => {
      for (const chart of root.querySelectorAll('.recharts-wrapper')) {
        const elements = [...chart.querySelectorAll<SVGTextElement>('[data-chart-value-label]')];
        if (!elements.length) continue;
        elements.forEach((el) => {
          el.style.display = '';
        });
        const svg = elements[0].ownerSVGElement;
        if (!svg) continue;
        const horizontal = [...chart.querySelectorAll<SVGLineElement>('.recharts-cartesian-grid-horizontal line')];
        const ys = horizontal.map((el) => Number(el.getAttribute('y1')));
        const bounds = {
          left: Math.min(...horizontal.map((el) => Number(el.getAttribute('x1')))),
          right: Math.max(...horizontal.map((el) => Number(el.getAttribute('x2')))),
          top: Math.min(...ys),
          bottom: Math.max(...ys),
        };
        if (!ys.length) {
          bounds.left = 0;
          bounds.top = 0;
          bounds.right = svg.viewBox.baseVal.width;
          bounds.bottom = svg.viewBox.baseVal.height;
        }
        const input: ValueLabelBox[] = elements.map((el) => {
          const text = el.getBBox(),
            d = el.dataset;
          return {
            x: 0,
            y: 0,
            width: text.width,
            height: text.height,
            barX: Number(d.barX),
            barY: Number(d.barY),
            barWidth: Number(d.barWidth),
            barHeight: Number(d.barHeight),
            kind: d.kind as ValueLabelBox['kind'],
            visible: true,
            inside: false,
          };
        });
        layoutValueLabels(input, bounds).forEach((p, i) => {
          const el = elements[i];
          el.setAttribute('x', String(p.x));
          el.setAttribute('y', String(p.y));
          el.setAttribute('fill', p.inside ? '#fff' : el.dataset.outsideFill);
          el.dataset.placement = p.inside ? 'inside' : 'above';
          el.style.display = p.visible ? '' : 'none';
        });
      }
    };
    place();
    const mutation = new MutationObserver(place);
    mutation.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-bar-x', 'data-bar-y', 'data-bar-width', 'data-bar-height', 'data-value'],
    });
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    resize?.observe(root);
    return () => {
      mutation.disconnect();
      resize?.disconnect();
    };
  }, [ref]);
}
